import {
  ArrowLeft,
  Tag,
  ShieldAlert,
  Check,
  User,
  MessageSquare,
  MoreHorizontal,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useState } from 'react';

interface ReportOrderScreenProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  customerName: string;
  category: string;
}

type ReportReason =
  | 'category-mismatch'
  | 'inappropriate-content'
  | 'other'
  | null;

export function ReportOrderScreen({
  isOpen,
  onClose,
  onSubmit,
  customerName,
  category,
}: ReportOrderScreenProps) {
  const [selectedReason, setSelectedReason] = useState<ReportReason>(null);

  const handleSubmit = () => {
    if (selectedReason) {
      onSubmit(selectedReason);
    }
  };

  const reportOptions = [
    {
      id: 'category-mismatch' as ReportReason,
      icon: Tag,
      title: 'Categoria incorreta',
      description: 'Este pedido não pertence à categoria anunciada',
    },
    {
      id: 'inappropriate-content' as ReportReason,
      icon: ShieldAlert,
      title: 'Conteúdo impróprio',
      description: 'Informações ofensivas ou inadequadas',
    },
    {
      id: 'other' as ReportReason,
      icon: MoreHorizontal,
      title: 'Outro',
      description: 'Outro motivo para denúncia',
    },
  ];

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0, // inset-0
            backgroundColor: '#ffffff', // bg-white
            zIndex: 50, // z-50
            display: 'flex',
            flexDirection: 'column', // flex flex-col
            maxWidth: '448px', // max-w-md
            marginLeft: 'auto', // mx-auto
            marginRight: 'auto',
          }}
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 35,
            duration: 0.25,
          }}
        >
          {/* Header */}
          <header
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              backgroundColor: '#ffffff',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '20px', // px-5
                paddingRight: '20px',
                paddingTop: '16px',
                paddingBottom: '16px',
              }}
            >
              <motion.button
                type="button"
                onClick={onClose}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px', // gap-2
                  transition: 'color 150ms ease',

                  color: 'var(--muted-foreground)',
                  cursor: 'pointer',
                  padding: '12px',
                  margin: '-12px',
                  borderRadius: 'var(--radius-button)',
                  backgroundColor: 'transparent',
                  border: 'none',
                  position: 'relative',
                  zIndex: 10,
                }}
                whileTap={{ scale: 0.95 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--foreground)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--muted-foreground)';
                }}
              >
                <ArrowLeft
                  className="w-5 h-5"
                  style={{ width: '20px', height: '20px' }}
                />
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  Voltar
                </span>
              </motion.button>

              <h1
                style={{
                  flex: 1, // flex-1
                  textAlign: 'center',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--foreground)',
                  marginLeft: '-60px', // Compensate button width for center alignment
                }}
              >
                Denunciar pedido
              </h1>
            </div>
          </header>

          {/* Content */}
          <div
            style={{
              flex: 1, // flex-1
              overflowY: 'auto', // overflow-y-auto
              paddingTop: '24px',
              paddingBottom: '24px',
            }}
          >
            {/* Order Summary Card - Now First */}
            <div
              style={{
                paddingLeft: '20px',
                paddingRight: '20px',
                marginBottom: '24px',
              }}
            >
              <div
                style={{
                  backgroundColor: 'var(--muted)',
                  borderRadius: 'var(--radius-card)',
                  padding: '16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px', // gap-3
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--background)',
                    }}
                  >
                    <User
                      className="w-5 h-5"
                      style={{
                        width: '20px',
                        height: '20px',
                        color: 'var(--muted-foreground)',
                      }}
                    />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        fontSize: 'var(--text-base)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--foreground)',
                        marginBottom: '2px',
                        lineHeight: '1.4',
                      }}
                    >
                      {customerName}
                    </p>
                    <p
                      style={{
                        fontSize: 'var(--text-sm)',
                        color: 'var(--muted-foreground)',
                        lineHeight: '1.4',
                      }}
                    >
                      {category}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Instruction */}
            <div
              style={{
                paddingLeft: '20px',
                paddingRight: '20px',
                marginBottom: '16px',
              }}
            >
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  lineHeight: '1.5',
                }}
              >
                Selecione o motivo da denúncia:
              </p>
            </div>

            {/* Report Options */}
            <div
              style={{
                paddingLeft: '20px',
                paddingRight: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              {reportOptions.map((option, index) => {
                const Icon = option.icon;
                const isSelected = selectedReason === option.id;

                return (
                  <motion.button
                    key={option.id}
                    onClick={() => setSelectedReason(option.id)}
                    style={{
                      width: '100%', // w-full
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 150ms ease', // transition-all

                      padding: '16px',
                      borderRadius: 'var(--radius-card)',
                      backgroundColor: isSelected
                        ? 'var(--kojo-blue-subtle)'
                        : 'transparent',
                      border: isSelected
                        ? '2px solid var(--kojo-blue)'
                        : '1px solid var(--border)',
                      gap: '16px',
                      cursor: 'pointer',
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                    whileTap={{ scale: 0.98 }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'var(--muted)';
                        e.currentTarget.style.borderColor =
                          'var(--muted-foreground)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderColor = 'var(--border)';
                      }
                    }}
                    type="button"
                  >
                    {/* Icon */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,

                        width: '40px',
                        height: '40px',
                        borderRadius: 'var(--radius-button)',
                        backgroundColor: isSelected
                          ? 'var(--kojo-blue-light)'
                          : 'var(--background)',
                        color: isSelected
                          ? 'var(--kojo-blue)'
                          : 'var(--foreground)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <Icon
                        className="w-5 h-5"
                        style={{ width: '20px', height: '20px' }}
                      />
                    </div>

                    {/* Text Content */}
                    <div
                      style={{
                        flex: 1,
                        textAlign: 'left',
                      }}
                    >
                      <p
                        className="text-foreground"
                        style={{
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-weight-semibold)',
                          lineHeight: '1.4',
                          marginBottom: '2px',
                        }}
                      >
                        {option.title}
                      </p>
                      <p
                        className="text-muted-foreground"
                        style={{
                          fontSize: 'var(--text-sm)',
                          lineHeight: '1.4',
                        }}
                      >
                        {option.description}
                      </p>
                    </div>

                    {/* Check Indicator */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'all 150ms ease',

                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: isSelected
                          ? 'var(--kojo-blue)'
                          : 'transparent',
                        border: isSelected ? 'none' : '2px solid var(--border)',
                      }}
                    >
                      {isSelected && (
                        <motion.div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{
                            type: 'spring',
                            stiffness: 500,
                            damping: 30,
                          }}
                        >
                          <Check
                            className="w-4 h-4"
                            style={{
                              display: 'block',
                              width: '16px',
                              height: '16px',
                              color: 'var(--background)',
                              strokeWidth: '3px',
                            }}
                          />
                        </motion.div>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Extra spacing for fixed footer */}
            <div style={{ height: '100px' }} />
          </div>

          {/* Footer with CTA - Fixed */}
          <motion.div
            style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 20,
              backgroundColor: '#ffffff',
              borderTop: '1px solid var(--border)',
              padding: '20px',
              boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
            }}
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              delay: 0.2,
              type: 'spring',
              stiffness: 200,
              damping: 25,
            }}
          >
            <motion.button
              onClick={handleSubmit}
              disabled={!selectedReason}
              style={{
                width: '100%', // w-full
                transition: 'all 150ms ease', // transition-all

                backgroundColor: selectedReason
                  ? 'var(--destructive)'
                  : 'var(--muted)',
                borderRadius: 'var(--radius-card)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-semibold)',
                paddingTop: '14px',
                paddingBottom: '14px',
                minHeight: '48px',
                color: selectedReason
                  ? 'var(--destructive-foreground)'
                  : 'var(--muted-foreground)',
                cursor: selectedReason ? 'pointer' : 'not-allowed',
                border: 'none',
              }}
              whileTap={selectedReason ? { scale: 0.98 } : {}}
              type="button"
            >
              Enviar denúncia
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
